import { useSetAtom } from "jotai";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";

import { useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionsActions, useCompositionsList } from "@design-lab/entities/composition";
import { cn } from "@design-lab/shared";
import { BACKGROUND_TYPE } from "@design-lab/shared/constants";
import { useShowFinalVerificationMobileVariant2 } from "@design-lab/shared/lib/config";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { artboardBackgroundAtom } from "@design-lab/widgets/artboard/model/state";
import { usePlacementPosition } from "@design-lab/widgets/artboard/model/usePlacementPosition";
import { AddToCart } from "@design-lab/widgets/footer-desktop/ui/AddToCart";
import { CompositionsList } from "./CompositionsList";
import { SummaryArtboard } from "./SummaryArtboard";

export function SummaryTab() {
  const { formCompositions, products, compositions } = useCompositionsList();
  const { removeComposition } = useCompositionsActions();

  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);

  const isMobileVariant2 = useShowFinalVerificationMobileVariant2();
  const setBackgroundType = useSetAtom(artboardBackgroundAtom);
  const { setTab } = useTab();
  const [isMinimized, setIsMinimized] = useState(true);

  useEffect(() => {
    if (isMobileVariant2) {
      setBackgroundType(BACKGROUND_TYPE.GREEN);
    }
    // Reset to front placement when summary tab is shown (only runs on mount)
    currentPlacement.setPlacement(selectedFrontPlacement.placement || "front");
    setTab("front");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileVariant2, setBackgroundType]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="relative mb-6 flex-1 p-1">
        <SummaryArtboard />

        <div className="absolute -bottom-6 left-1 right-1 z-10 flex flex-col shadow">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="bg-background flex h-10 w-full items-center justify-between rounded-t-lg border-t p-2 hover:bg-gray-50"
            data-testid="summary-toggle-button"
          >
            <span className="text-sm">Summary</span>
            {isMinimized ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
          </button>

          <div
            className={cn(
              "bg-background overflow-hidden transition-all duration-300",
              isMinimized ? "max-h-0" : "max-h-[40vh]"
            )}
          >
            <div className="h-full max-h-[40vh] space-y-4 overflow-y-auto p-2">
              <CompositionsList
                formCompositions={formCompositions}
                compositions={compositions}
                products={products}
                onRemoveComposition={removeComposition}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-background mt-1 border-t p-2">
        <AddToCart />
      </div>
    </div>
  );
}
